import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Briefcase, Loader2, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact CTA banner for the ₹101 Job Match Report.
 * Auto-hides for high-school users (they don't need it yet).
 *
 * Modes:
 *  - career + educationLevel → active, opens Razorpay for that career
 *  - variant="info" → informational badge on Results (no specific career yet)
 */
export default function JobsCTABanner({ career, educationLevel, variant }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (educationLevel === "high_school") return null;

  // Informational variant — used on Results & Landing before user picks a career
  if (variant === "info") {
    return (
      <div
        data-testid="jobs-cta-info"
        className="border border-[#4C1D95] bg-[#5B21B6] text-white rounded-sm p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 fade-up"
      >
        <div className="w-10 h-10 bg-[#FACC15] flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-0.5">
            NEW &middot; Ready to apply for jobs?
          </div>
          <div className="font-heading text-lg md:text-xl font-bold leading-tight">
            Unlock real openings for any career &mdash; upload your resume, get matches on
            <span className="text-[#FACC15]"> LinkedIn, Naukri, Indeed &amp; Shine</span>
          </div>
          <div className="text-xs text-white/80 mt-1.5 leading-relaxed">
            Click any career above &rarr; look for the <b>&quot;Get job matches &middot; &#8377;101&quot;</b> button
            on the career detail page. Includes an AI gap-analysis of your resume vs the role.
          </div>
        </div>
        <div
          data-testid="jobs-cta-info-price"
          className="bg-[#FACC15] text-[#0A0A0A] px-4 py-3 rounded-sm font-heading font-bold text-lg shrink-0 flex flex-col items-center leading-none"
        >
          <span className="font-mono text-[9px] tracking-widest text-[#0A0A0A]/70 mb-1">JOB MATCH</span>
          <span>&#8377;101</span>
        </div>
      </div>
    );
  }

  if (!career?.career_id || !career?.title) return null;

  const startPayment = async () => {
    if (!window.Razorpay) {
      toast.error("Payment library not loaded. Please refresh the page.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/payments/order", {
        package_id: "career_jobs",
        career_id: career.career_id,
        career_title: career.title,
      });
      const { order_id, amount, currency, key_id, product } = res.data;
      const options = {
        key: key_id,
        amount,
        currency,
        name: "CareerWisdom",
        description: product,
        order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        notes: { user_id: user?.user_id, package: "career_jobs", career_id: career.career_id },
        theme: { color: "#0A0A0A" },
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            window.location.href = `/jobs/${response.razorpay_order_id}`;
          } catch {
            toast.error("Payment verification failed. Please contact support.");
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => {
        toast.error(`Payment failed: ${r.error?.description || "Please try again."}`);
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="jobs-cta-banner"
      className="border border-[#4C1D95] bg-[#5B21B6] text-white rounded-sm p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 fade-up"
    >
      <div className="w-10 h-10 bg-[#FACC15] flex items-center justify-center shrink-0">
        <Briefcase className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-0.5">
          Ready to apply? · Job Match Report · ₹101
        </div>
        <div className="font-heading text-lg md:text-xl font-bold leading-tight">
          Upload your resume &mdash; get real openings for <span className="text-[#FACC15]">{career.title}</span>
        </div>
        <div className="text-xs text-white/80 mt-1.5 leading-relaxed">
          Personalized links to <b>LinkedIn</b>, <b>Naukri</b>, <b>Indeed</b> &amp; <b>Shine</b> +
          an AI gap-analysis of your resume vs this career &middot; strengths, skill gaps, and edits to make right now.
        </div>
      </div>
      <button
        onClick={startPayment}
        disabled={loading}
        data-testid="buy-jobs-button"
        className="bg-[#FACC15] text-[#0A0A0A] px-5 py-3 font-semibold rounded-sm hover:-translate-y-0.5 transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</>
        ) : (
          <><Sparkles className="w-4 h-4" /> Get job matches · ₹101</>
        )}
      </button>
    </div>
  );
}
